/*
  ECSR-1292 (HOBO-869): GA4 click tracking for the Brand hero
  (sections/hobo-hero.liquid) and Rich text (brand refresh)
  (sections/hobo-rich-text.liquid) sections.

  Events reach GTM the same way the Quick Link Nav, Product Carousel, breadcrumb
  and quiz events do: Shopify.analytics.publish(<event>, <params>), picked up by
  the store's GTM custom pixel.

    cta_click_brand_hero       CTA 1, CTA 2, or the block body (body = cta_1)
    cta_click_brand_hero_logo  the signature (logo) image, which links to the
                               homepage (data-brand-track-cta="logo")
    cta_click_rich_text        a link inside a Rich text (brand refresh) section;
                               a section without links never fires

  Each section opts in with data attributes on its root and links, so every
  instance on every page is tracked independently. One delegated listener on the
  document reads those attributes at click time, which also covers sections the
  theme editor re-renders. A plain listener rather than theme.delegate: the
  binding is on the document, not on a section, and this file may run before the
  deferred theme bundle that defines `window.on`.

  Deferred delivery for same-tab navigations: a published event reaches GA4
  through the custom pixel's sandbox, GTM inside it, and GA4's own batching —
  measured at 5-6 seconds after the click. A page that navigates away within
  that time loses the event, and holding the navigation that long is not
  acceptable. So an ordinary same-tab click to a page on this storefront is not
  published here: the event (name and full parameters, page path
  and URL taken from this page at click time) is queued in sessionStorage under
  `hoboBrandTrackPending`, and the link navigates at once, as a normal link
  does. "This storefront" uses the same www/apex-tolerant host match as
  p009_linkclick_url; because sessionStorage is per-origin, a link written
  against the other form of the host (www vs apex) is navigated to this page's
  origin with the same path, query and hash, so the queue is readable on
  arrival. snippets/brand-block-tracking-flush.liquid, rendered from
  layout/theme.liquid on every storefront page, publishes the queue on the next
  page load; the payload is exactly what would have been published here.

  Published at once, as before: modifier clicks, middle clicks,
  target="_blank", downloads, same-page anchors and non-http links (mailto:,
  tel:), where this page stays.

  Known limitation: a same-tab click to another site (where the queue cannot be
  read), or any same-tab click when sessionStorage is unavailable, publishes
  immediately and holds the navigation only 150ms. That is far shorter than
  the 5-6 second delivery time, so these events are expected to be lost: the
  page unloads before the pixel delivers them. A longer hold is deliberately
  not used.

  Brand hero body clicks: the section's own click delegate navigates to CTA 1
  unless the event was already prevented. This listener runs first (capture
  phase), applies the same gate that delegate uses, queues (or publishes),
  prevents, and performs the same navigation — immediately when queued, after
  the 150ms hold otherwise. Without this file the delegate behaves exactly as
  before.

  Debug: add ?debug_tracking=1 to the URL to log each event to the console.
*/
(function () {
  'use strict';

  if (window.hoboBrandBlockTracking) return;
  window.hoboBrandBlockTracking = true;

  var NAV_DELAY = 150;
  var PENDING_KEY = 'hoboBrandTrackPending'; // read by snippets/brand-block-tracking-flush.liquid
  var MAX_VALUE_LENGTH = 100; // GA4 truncates event parameter values at 100 characters
  var MOBILE_QUERY = '(max-width: 767px)'; // the breakpoint the hero's media switches at
  var DEBUG = /[?&]debug_tracking=1/.test(window.location.search);

  /* Lowercase, symbols stripped, spaces to single underscores, trimmed — the same
     rule the quiz block's p050 value uses. */
  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, MAX_VALUE_LENGTH)
      .replace(/_$/, '');
  }

  /* Text of a rendered heading, with a space between its text nodes so that
     separate lines (<p>, <br>) do not run together. */
  function textOf(el) {
    var parts = [];
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) parts.push(node.nodeValue);
    return parts.join(' ');
  }

  function contentTitle(root, kind) {
    var selectors = kind === 'hero'
      ? ['.hobo-hero__title', '.hobo-hero__eyebrow']
      : ['.hobo-richtext__heading'];
    for (var i = 0; i < selectors.length; i++) {
      var el = root.querySelector(selectors[i]);
      if (!el) continue;
      var title = normalise(textOf(el));
      if (title) return title;
    }
    return '';
  }

  function assetInfo(root) {
    var raw = root.getAttribute('data-track-asset') || '';
    var mobile = root.getAttribute('data-track-asset-mobile') || '';
    if (mobile && window.matchMedia && window.matchMedia(MOBILE_QUERY).matches) raw = mobile;
    var split = raw.indexOf(':');
    if (split < 0) return normalise(raw) || 'none';
    /* Shopify appends `_<uuid>` to a re-uploaded file name; the asset is named
       without it, as it was uploaded. */
    var asset = raw.slice(split + 1).replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
    return normalise(raw.slice(0, split)) + ':' + normalise(asset);
  }

  function sameSite(hostname) {
    return hostname.replace(/^www\./, '') === window.location.hostname.replace(/^www\./, '');
  }

  /* Internal links as a lowercase path (`/collections/new-arrivals`), external
     http(s) links as origin + path, anything else as written. Query strings are
     dropped, as the breadcrumb event does. */
  function linkUrl(href) {
    if (!href) return '';
    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return href;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return href;
    if (sameSite(url.hostname)) return url.pathname.toLowerCase();
    return (url.origin + url.pathname).toLowerCase();
  }

  function baseParams(root, kind, href) {
    var params = {
      p009_linkclick_url: linkUrl(href),
      p001_page_path: window.location.pathname,
      p032_page_url: window.location.href
    };
    if (kind === 'hero') params.p015_asset_info = assetInfo(root);
    params.p019_content_title = contentTitle(root, kind);
    return params;
  }

  function publish(name, params) {
    if (DEBUG) console.debug('[brand-block-tracking]', name, params);
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, params);
      }
    } catch (e) {
      if (DEBUG) console.error('[brand-block-tracking]', e);
    }
  }

  /* Queue an event for the next page load. False when sessionStorage is not
     usable, so the caller can publish now instead. */
  function queue(name, params) {
    try {
      var list = JSON.parse(window.sessionStorage.getItem(PENDING_KEY) || '[]');
      if (!Array.isArray(list)) list = [];
      list.push({ name: name, params: params, t: Date.now() });
      window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(list));
      if (DEBUG) console.debug('[brand-block-tracking] queued for next page', name, params);
      return true;
    } catch (e) {
      return false;
    }
  }

  /* The queue is per-origin, so only a destination on this origin can flush it. */
  function sameOrigin(href) {
    try {
      return new URL(href, window.location.href).origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  /* Where a queued navigation should go: the href itself when it is on this
     origin; this origin + path, query and hash when it is the same site under
     the other host form (www/apex); null when it is another site or not
     http(s), so the event cannot be queued. */
  function queueTarget(href) {
    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.origin === window.location.origin) return url.href;
    if (!sameSite(url.hostname)) return null;
    return window.location.origin + url.pathname + url.search + url.hash;
  }

  function navigateLater(href) {
    setTimeout(function () {
      window.location.href = href;
    }, NAV_DELAY);
  }

  /* True only for a click that would replace this page in the same tab. */
  function holdsNavigation(event, link) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    var target = (link.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    if (link.protocol !== 'http:' && link.protocol !== 'https:') return false;
    if (link.hash && link.pathname === window.location.pathname && link.search === window.location.search && link.host === window.location.host) return false;
    return true;
  }

  function trackLink(event, link, name, params) {
    if (!holdsNavigation(event, link)) {
      publish(name, params);
      return;
    }
    /* Same-tab navigation to this site: queue and let the link navigate as
       normal, or to this origin when it was written against the other host
       form. Off-site: publish and hold briefly (expected to be lost, see top). */
    var dest = queueTarget(link.href);
    if (dest && queue(name, params)) {
      if (!sameOrigin(link.href)) {
        event.preventDefault();
        window.location.href = dest;
      }
      return;
    }
    publish(name, params);
    event.preventDefault();
    navigateLater(link.href);
  }

  function onClick(event) {
    if (event.defaultPrevented) return;
    var target = event.target;
    if (!target || !target.closest) return;
    var root = target.closest('[data-brand-track]');
    if (!root) return;

    var kind = root.getAttribute('data-brand-track');
    var link = target.closest('a[href]');
    if (link && !root.contains(link)) link = null;

    if (kind === 'rich_text') {
      if (!link) return;
      trackLink(event, link, 'cta_click_rich_text', baseParams(root, kind, link.getAttribute('href')));
      return;
    }

    if (kind !== 'hero') return;
    var params;

    if (link) {
      params = baseParams(root, kind, link.getAttribute('href'));
      if (link.matches('[data-brand-track-cta="logo"]')) {
        trackLink(event, link, 'cta_click_brand_hero_logo', params);
        return;
      }
      params.p018_cta_position = link.getAttribute('data-brand-track-cta') === 'cta_2' ? 'cta_2' : 'cta_1';
      trackLink(event, link, 'cta_click_brand_hero', params);
      return;
    }

    /* Not a link: the same gate the section's own body-click delegate applies. */
    if (target.closest('button, input, select, textarea, iframe, [role="button"]')) return;
    var selection = window.getSelection && window.getSelection();
    if (selection && selection.type === 'Range' && selection.toString().length > 0) return;

    var bodyHref = root.getAttribute('data-track-href') || '';
    params = baseParams(root, kind, bodyHref);

    if (!bodyHref) return; // a hero with no body link: the click goes nowhere and is not a CTA
    params.p018_cta_position = 'cta_1';
    event.preventDefault();
    var bodyDest = queueTarget(bodyHref);
    if (bodyDest && queue('cta_click_brand_hero', params)) {
      window.location.href = bodyDest;
      return;
    }
    publish('cta_click_brand_hero', params);
    navigateLater(bodyHref);
  }

  document.addEventListener('click', onClick, true);
})();
